<template>
  <div class="dea-card-resource__card-header--right">
    <div class="dea-card-resource__card-header-spacer">&nbsp;</div>
    <span
      v-if="(!showImage || !resource.image) && showTypeIcon && layout === 'compressed'"
      class="dea-card-resource__icon dea-card-resource__icon--header-right"
      :class="`${resource.type_icon} ${iconAccentClass}`"
    ></span>
    <Badge v-if="teachersOnly && showTeacherLabel" class="dea-badge dea-badge__app dea-bg-grey-dark">Docente</Badge>
    <Buttons
      v-if="isLocked"
      :class="[
        { active: resource.buttonsActive },
        actionAccentClass,
        { 'dea-button--outline dea-button--circle dea-button--large': !isSvg(lockIcon) },
        { 'dea-button--transparent': isSvg(lockIcon) },
      ]"
      :icon="lockIcon"
      type="button"
      @click="resourceLocked"
    ></Buttons>
    <Buttons
      v-if="shareEnabled && !isLocked && (!teachersOnly || $root.config.share_resource_teacher_enabled)"
      :class="[
        { active: resource.buttonsActive },
        { 'dea-button--outline dea-button--circle dea-button--large dea-button-share': !isSvg(shareIcon) },
        { 'dea-button--transparent': isSvg(shareIcon) },
        actionAccentClass,
        { 'dea-button-share--disabled': !resource.share_url },
      ]"
      :icon="shareIcon"
      type="button"
      @click.stop.prevent="openDropdownShare"
    ></Buttons>
    <Buttons
      v-if="bookmarkEnabled && !isLocked"
      :class="[
        { active: resource.buttonsActive },
        { 'dea-button--circle dea-button--large': !isSvg(bookmarkIcon) },
        { 'dea-button--transparent': isSvg(bookmarkIcon) },
        actionAccentClass,
        bookmarkButtonStatusClass,
        { 'dea-button-share--disabled': resource.assets && resource.assets.length > 1 },
      ]"
      :icon="bookmarkIcon"
      type="button"
      @click.stop.prevent="bookmarkButtonAction"
    ></Buttons>
    <Tooltip v-if="showConfirmDelete" :negative="true" :position="tooltipArrow.position" :side="tooltipArrow.side">
      <div class="dea-tooltip__content dea-heading--regular-xs">
        Vuoi eliminare questa <br />
        risorsa dall’elenco dei <br />
        preferiti?
      </div>
      <div class="dea-tooltip__actions dea-text--right">
        <Buttons class="dea-button--small dea-tooltip__action--primary" @click.stop.prevent="deleteBookmark"
          >Sì, rimuovi dall'elenco</Buttons
        >
        <Links
          class-name="dea-links--small dea-tooltip__action--secondary dea-links--pointer"
          @click.stop.prevent="showConfirmDelete = false"
          >No, tienila in elenco</Links
        >
      </div>
    </Tooltip>
    <Tooltip
      v-if="showConfirmSave"
      class="dea-tooltip__bookmark-saved"
      :negative="false"
      :position="tooltipArrow.position"
      :side="tooltipArrow.side"
    >
      <p class="dea-font-white">
        Salvato tra i preferiti!<br />
        Trovi tutti i tuoi preferiti in Area Personale!
      </p>
    </Tooltip>
    <ShareButtons
      v-if="dropdownShare"
      :shared-object="resource"
      :warning-text="teachersResourceWarning"
      :config="$root.config"
      @close-dropdown="dropdownShare = $event"
    >
      <template slot="content">
        <slot name="content"></slot>
      </template>
    </ShareButtons>
  </div>
</template>

<script>
import Buttons from "@/components/elements/Buttons";
import Badge from "@/components/elements/Badge";
import ShareButtons from "@/components/patterns/ShareButtons";
import Tooltip from "@/components/elements/Tooltip";
import Links from "@/components/elements/Links";

export default {
  name: "CardResourceActionButtons",
  components: { ShareButtons, Buttons, Tooltip, Badge, Links },
  props: {
    /**
     * Resource to render
     */
    resource: {
      type: Object,
      required: true,
    },
    /**
     * Flag to show the resource image
     */
    showImage: {
      type: Boolean,
      default: true,
    },
    /**
     * Flag to show type_format icon
     */
    showTypeIcon: {
      type: Boolean,
      default: true,
    },
    /**
     * CardResource layout selection. Possible values: "expanded", "light", "compressed".
     */
    layout: {
      type: String,
      default: "expanded",
      validator: value => {
        return ["expanded", "compressed", "light"].indexOf(value) !== -1;
      },
    },
    /**
     * Class to set type icon accent color.
     */
    iconAccentClass: {
      type: String,
      default: "",
    },
    /**
     * Class to set action buttons accent color.
     */
    actionAccentClass: {
      type: String,
      default: "",
    },
    /**
     * Resource targeted at teachers.
     */
    teachersOnly: {
      type: Boolean,
      default: true,
    },
    /**
     * Text to show when sharing a teachers-only resource
     */
    teachersResourceWarning: {
      type: String,
      default: "",
    },
    /**
     * Flag to show the teacher label for teacher resources
     */
    showTeacherLabel: {
      type: Boolean,
      default: true,
    },
    /**
     * Flag to mark the resource as lock / unavailable
     */
    isLocked: {
      type: Boolean,
      default: true,
    },
    /**
     * Flag to show the share button with dropdown
     */
    shareEnabled: {
      type: Boolean,
      default: true,
    },
    /**
     * Flag to show the bookmark button
     */
    bookmarkEnabled: {
      type: Boolean,
      default: true,
    },
    /**
     * Flag to show the bookmark button
     */
    bookmarkButtonStatus: {
      type: Boolean,
      default: false,
    },
    /**
     * Tooltip arrow settingsa
     */
    tooltipArrow: {
      type: Object,
      default: () => ({
        position: "right",
        side: "top",
      }),
    },
    shareIcon: {
      type: String,
      default: "share",
    },
    lockIcon: {
      type: String,
      default: "locker",
    },
    bookmarkIcon: {
      type: String,
      default: "preferiti",
    },
  },
  data() {
    return {
      dropdownShare: false,
      showConfirmDelete: false,
      showConfirmSave: false,
    };
  },
  computed: {
    bookmarkButtonStatusClass() {
      return !this.bookmarkButtonStatus ? "dea-button--outline" : "";
    },
  },
  watch: {
    bookmarkButtonStatus() {
      if (!this.bookmarkButtonStatus) this.showConfirmDelete = false;
    },
  },
  methods: {
    isSvg(icon) {
      return icon.endsWith(".svg");
    },
    bookmarkButtonAction() {
      if (this.bookmarkButtonStatus) {
        this.showConfirmDelete = true;
      } else {
        this.$emit("save-bookmark");
        this.showConfirmSave = true;
        setTimeout(() => {
          this.showConfirmSave = false;
        }, 3500);
      }
    },
    resourceLocked() {
      this.$emit("resource-locked");
    },
    deleteBookmark() {
      this.$emit("delete-bookmark");
    },
    openDropdownShare() {
      if (this.resource.share_url) {
        this.dropdownShare = !this.dropdownShare;
        this.$emit("dropdown-toggled", this.dropdownShare);
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/styles/system.utils.scss"; //sass-lint:disable-line clean-import-paths

$bookmark-save-tooltip-size: 320px;

.dea-card-resource__card-header--right {
  .dea-tooltip {
    // sass-lint:disable-block no-duplicate-properties, no-vendor-prefixes
    max-width: $dea-size-xxx-large;
    position: absolute;
    top: $space-large;
    width: fit-content;
    width: intrinsic; /* Safari/WebKit uses a non-standard name */
    width: -moz-max-content; /* Firefox/Gecko */
    width: -webkit-max-content; /* Chrome */
    z-index: 1;

    &__inner {
      width: auto;
    }

    &__action--secondary {
      text-align: right;
    }

    &__bookmark-saved {
      max-width: unset;
      min-width: $bookmark-save-tooltip-size;
    }
  }
}
</style>
